import { of, concat, merge, EMPTY } from "rxjs";
import { switchMap, mergeMap, delay, takeUntil } from "rxjs/operators";
import { ofType } from "redux-observable";
import { v4 as uuid } from "uuid";
import appConfig from "@/appConfig";

export const TOAST_PUSH = "pss/toast/PUSH";
export const TOAST_DISMISS = "pss/toast/DISMISS";
export const TOAST_ACTIVATE = "pss/toast/ACTIVATE";
export const TOAST_DEACTIVATE = "pss/toast/DEACTIVATE";

const initialState = {
  active: null,
  queued: [],
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case TOAST_PUSH:
      return {
        ...state,
        queued: [...state.queued, action.payload],
      };
    case TOAST_DISMISS:
      // MH-2024-05-28: only re-queue those messages that do not have the same local key
      // as the message that has been dismissed. So if there are different kinds of messages
      // always pop them up, but for the same kind of message only show one.
      let dismissedItem = state.queued.find((t) => t.id === action.payload.id);
      return {
        ...state,
        queued: dismissedItem
          ? state.queued.filter((t) => t.localKey !== dismissedItem.localKey)
          : state.queued.filter((t) => t.id !== action.payload.id),
      };
    case TOAST_ACTIVATE:
      return {
        ...state,
        active: state.queued[0] || null,
        queued: state.queued.slice(1),
      };
    case TOAST_DEACTIVATE:
      return {
        ...state,
        active: null,
        queued: state.active
          ? state.queued.filter((t) => t.localKey !== state.active.localKey)
          : state.queued,
      };
    default:
      return state;
  }
}

export function toastPush({
  id = uuid(),
  localKey,
  localScope,
  localContext,
  variant,
  action,
}) {
  return {
    type: TOAST_PUSH,
    payload: {
      id,
      localKey,
      localScope,
      localContext,
      action,
      variant,
    },
  };
}

export function toastDismiss(id) {
  return { type: TOAST_DISMISS, payload: { id } };
}

function toastActivate() {
  return { type: TOAST_ACTIVATE };
}

function toastDeactivate() {
  return { type: TOAST_DEACTIVATE };
}

function pushEpic(action$, state$) {
  const deactivate$ = of(toastDeactivate());
  const activate$ = concat(
    of(toastActivate()),
    deactivate$.pipe(delay(appConfig.toastTTL))
  ).pipe(takeUntil(action$.pipe(ofType(TOAST_DEACTIVATE))));

  const push$ = action$.pipe(
    ofType(TOAST_PUSH),
    mergeMap(() => {
      const { toast } = state$.value;
      return !toast.active && toast.queued.length === 1 ? activate$ : EMPTY;
    })
  );

  const loop$ = action$.pipe(
    ofType(TOAST_DEACTIVATE),
    switchMap(() => {
      const { toast } = state$.value;
      return toast.queued.length > 0
        ? activate$.pipe(delay(appConfig.toastPause))
        : EMPTY;
    })
  );

  return merge(push$, loop$);
}

function dismissEpic(action$, state$) {
  return action$.pipe(
    ofType(TOAST_DISMISS),
    switchMap(({ payload }) => {
      return state$.value.toast.active?.id === payload.id
        ? of(toastDeactivate())
        : EMPTY;
    })
  );
}

export const toastEpics = [pushEpic, dismissEpic];
